import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../config/api';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { pushHardwareBackHandler } from '../utils/hardwareBackBridge';
import { trackFunnelEvent } from '../utils/trackFunnelEvent';
import OptimizedImage from '../components/OptimizedImage';
import {
  CustomizationGroup,
  defaultCustomizationValues,
  getPriceDeltaForCustomizations,
  resolveCustomizationGroups,
} from '../config/drinkCustomizations';
import {
  formatTodayHours,
  getCafeClosedMessage,
  isCafeOpenNow,
  storeHoursFromCafe,
  type StoreHoursConfig,
} from '../utils/storeHours';

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  image_url?: string;
  is_available?: number | boolean;
  customization_config?: Record<string, string[]> | null;
  customization_groups?: string[] | Record<string, string[]> | string | null;
}

interface Cafe {
  id: string;
  name: string;
  address: string;
  phone: string;
  store_hours?: StoreHoursConfig;
  accepting_orders?: boolean | number;
  is_open_now?: boolean;
}

export default function CafeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { showToast } = useToast();
  const [cafe, setCafe] = useState<Cafe | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [filteredMenuItems, setFilteredMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [note, setNote] = useState('');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [customizationGroups, setCustomizationGroups] = useState<CustomizationGroup[]>([]);
  const [customizations, setCustomizations] = useState<Record<string, string>>({});
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [menuSearchTerm, setMenuSearchTerm] = useState('');
  const [storeHours, setStoreHours] = useState<StoreHoursConfig | null>(null);
  const [isOpenNow, setIsOpenNow] = useState(true);
  const menuViewTracked = useRef<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchCafe();
      fetchMenu();
      checkFavorite();
    }
  }, [id]);

  useEffect(() => {
    if (!id || loading || !cafe || menuViewTracked.current === id) return;
    menuViewTracked.current = id;
    trackFunnelEvent('menu_view', id);
  }, [id, loading, cafe]);

  useEffect(() => {
    if (!storeHours) return;
    const refresh = () => setIsOpenNow(isCafeOpenNow(storeHours));
    refresh();
    const timer = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(timer);
  }, [storeHours]);

  useEffect(() => {
    if (menuSearchTerm.trim()) {
      const filtered = menuItems.filter(item =>
        item.name.toLowerCase().includes(menuSearchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(menuSearchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(menuSearchTerm.toLowerCase())
      );
      setFilteredMenuItems(filtered);
    } else {
      setFilteredMenuItems(menuItems);
    }
  }, [menuSearchTerm, menuItems]);

  const closeCustomizeModal = useCallback(() => {
    setShowNoteModal(false);
    setNote('');
    setSelectedItem(null);
    setCustomizationGroups([]);
    setCustomizations({});
  }, []);

  useEffect(() => {
    if (!showNoteModal) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('customize-modal-open');
    return () => {
      document.body.style.overflow = prevOverflow;
      document.documentElement.classList.remove('customize-modal-open');
    };
  }, [showNoteModal]);

  useEffect(() => {
    if (!showNoteModal) return;
    return pushHardwareBackHandler(() => {
      closeCustomizeModal();
      return true;
    });
  }, [showNoteModal, closeCustomizeModal]);

  const checkFavorite = async () => {
    if (!id) return;
    try {
      const response = await axios.get(`/api/favorites/check/${id}`);
      setIsFavorite(response.data.isFavorite);
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!id || favoriteLoading) return;
    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        await axios.delete(`/api/favorites/${id}`);
        setIsFavorite(false);
      } else {
        await axios.post(`/api/favorites/${id}`);
        setIsFavorite(true);
      }
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      showToast(error.response?.data?.error || 'Failed to update favorite', 'error');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const fetchCafe = async () => {
    try {
      const response = await axios.get(`/api/cafes/${id}`);
      const data = response.data;
      setCafe(data);
      const hours = storeHoursFromCafe(data);
      setStoreHours(hours);
      setIsOpenNow(isCafeOpenNow(hours));
    } catch (error) {
      console.error('Error fetching cafe:', error);
    }
  };

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`/api/cafes/${id}/menu`);
      const items = (response.data || []).filter(
        (item: MenuItem) => item.is_available !== 0 && item.is_available !== false
      );
      setMenuItems(items);
      setFilteredMenuItems(items);
    } catch (error) {
      console.error('Error fetching menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCartClick = (item: MenuItem) => {
    if (!isOpenNow && storeHours) {
      showToast(getCafeClosedMessage(storeHours), 'error');
      return;
    }
    setSelectedItem(item);

    const groups = resolveCustomizationGroups(item);
    setCustomizationGroups(groups);
    setCustomizations(defaultCustomizationValues(groups));

    setNote('');
    setShowNoteModal(true);
  };

  const handleCustomizationChange = (groupId: string, value: string) => {
    setCustomizations(prev => ({
      ...prev,
      [groupId]: value,
    }));
  };

  const handleConfirmAddToCart = () => {
    if (!id || !selectedItem) return;
    if (!isOpenNow && storeHours) {
      showToast(getCafeClosedMessage(storeHours), 'error');
      return;
    }

    const hasCustomizations = customizationGroups.length > 0;
    const priceDelta = hasCustomizations
      ? getPriceDeltaForCustomizations(
          { category: selectedItem.category, name: selectedItem.name },
          customizations
        )
      : 0;
    const finalPrice = selectedItem.price + priceDelta;
    addItem(
      {
        menu_item_id: selectedItem.id,
        name: selectedItem.name,
        price: finalPrice,
        quantity: 1,
        category: selectedItem.category,
        note: note.trim() || undefined,
        customizations: hasCustomizations ? customizations : undefined,
      },
      id
    );
    setShowNoteModal(false);
    setNote('');
    setSelectedItem(null);
    setCustomizationGroups([]);
    setCustomizations({});
  };

  if (loading) {
    return <div className="container mx-auto px-4 py-8 text-center">Loading menu...</div>;
  }

  const categories = Array.from(new Set(menuItems.map(item => item.category)));

  return (
    <div className="container mx-auto px-4 py-8 pb-4">
      {cafe && (
        <div className="mb-6">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1 text-sm font-semibold text-amber-700 hover:text-amber-800 mb-2"
                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
              >
                <span className="text-lg leading-none">←</span>
                <span>Back to cafes</span>
              </button>
              <h1 className="text-3xl md:text-4xl font-bold text-amber-800">{cafe.name}</h1>
            </div>
            <button
              type="button"
              onClick={toggleFavorite}
              disabled={favoriteLoading}
              className={`ml-3 text-3xl transition ${isFavorite ? 'text-red-500' : 'text-gray-300'} hover:scale-110 ${favoriteLoading ? 'opacity-50' : ''}`}
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', pointerEvents: favoriteLoading ? 'none' : 'auto' }}
            >
              {isFavorite ? '❤️' : '🤍'}
            </button>
          </div>
          <p className="text-gray-600">📍 {cafe.address}</p>
          <p className="text-gray-600">📞 {cafe.phone}</p>
          {storeHours && (
            <p className="text-sm text-gray-500 mt-1">{formatTodayHours(storeHours)}</p>
          )}
        </div>
      )}

      {!isOpenNow && storeHours && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
          <p className="font-semibold">Closed for orders</p>
          <p className="text-sm mt-1">{getCafeClosedMessage(storeHours)}</p>
          <p className="text-sm mt-1 text-amber-800">You can browse the menu, but ordering is unavailable right now.</p>
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-4 text-amber-700">Menu</h2>
        <div className="relative">
          <input
            type="text"
            placeholder="Search menu items..."
            value={menuSearchTerm}
            onChange={(e) => setMenuSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
          />
          <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
        </div>
      </div>

      {menuSearchTerm && filteredMenuItems.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center dark:bg-gray-800 dark:text-gray-100">
          <p className="text-gray-600 dark:text-gray-400 mb-4">No menu items found matching "{menuSearchTerm}"</p>
          <button
            onClick={() => setMenuSearchTerm('')}
            className="text-amber-700 hover:text-amber-800 font-medium dark:text-amber-400"
          >
            Clear search
          </button>
        </div>
      ) : (
        (menuSearchTerm ? Array.from(new Set(filteredMenuItems.map(item => item.category))) : categories).map(category => (
        <div key={category} className="mb-8">
          <h3 className="text-xl font-semibold mb-4 text-amber-800 border-b-2 border-amber-200 pb-2">
            {category}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(menuSearchTerm ? filteredMenuItems : menuItems)
              .filter(item => item.category === category)
              .map(item => (
                <div
                  key={item.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition"
                >
                  {item.image_url && (
                    <div className="h-40 bg-amber-100 overflow-hidden relative">
                      <OptimizedImage
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        placeholder={item.name}
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h4 className="font-bold text-lg mb-2">{item.name}</h4>
                    <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-amber-700 font-bold text-lg">{item.price.toFixed(2)} JOD</span>
                      <button
                        onClick={() => handleAddToCartClick(item)}
                        disabled={!isOpenNow}
                        className="bg-amber-700 text-white px-4 py-2 rounded hover:bg-amber-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isOpenNow ? 'Add to Cart' : 'Closed'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )))}

      <div className="mt-8">
        <button
          onClick={() => navigate('/cart')}
          className="bg-amber-700 text-white px-6 py-3 rounded-lg hover:bg-amber-800 transition"
        >
          View Cart
        </button>
      </div>

      {showNoteModal &&
        selectedItem &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="customize-drink-overlay fixed inset-0 z-[200] flex flex-col justify-end sm:items-center sm:justify-center sm:p-4"
            role="presentation"
          >
            <button
              type="button"
              className="absolute inset-0 bg-black/55"
              aria-label="Close customization"
              onClick={closeCustomizeModal}
            />

            <div
              className="relative z-10 mx-auto flex w-full max-w-md flex-col sm:max-h-[90vh]"
              role="dialog"
              aria-labelledby="customize-drink-title"
              aria-modal="true"
            >
              <div className="flex max-h-[min(58dvh,520px)] flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl dark:bg-gray-800 sm:rounded-lg">
                <div className="shrink-0 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
                  <h3
                    id="customize-drink-title"
                    className="text-xl font-bold text-amber-800 dark:text-amber-200"
                  >
                    Customize your drink
                  </h3>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">{selectedItem.name}</p>
                </div>

                <div className="modal-sheet-scroll min-h-0 flex-1 px-5 py-4">
                  {customizationGroups.length > 0 && (
                    <div className="mb-4 space-y-4">
                      {customizationGroups.map((group) => (
                        <div key={group.id}>
                          <p className="mb-1 text-sm font-semibold text-amber-800 dark:text-amber-200">
                            {group.label}
                          </p>
                          <select
                            className="mt-1 w-full rounded-lg border border-amber-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                            value={customizations[group.id] || ''}
                            onChange={(e) =>
                              handleCustomizationChange(group.id, e.target.value)
                            }
                          >
                            {group.options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {group.options.some((o) => o.helperText) && (
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {group.options.find((o) => o.value === customizations[group.id])
                                ?.helperText ||
                                group.options.find((o) => o.helperText)?.helperText}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Any extra notes? (e.g., 'No sugar', 'Extra hot', 'Light ice on top')"
                    className="w-full rounded-lg border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                    rows={3}
                  />
                </div>
              </div>

              <div className="modal-sheet-footer flex shrink-0 gap-3 border-t border-gray-200 bg-white px-5 pt-4 shadow-[0_-4px_20px_rgba(0,0,0,0.12)] dark:border-gray-700 dark:bg-gray-800">
                <button
                  type="button"
                  onClick={closeCustomizeModal}
                  className="flex-1 rounded-lg bg-gray-200 px-4 py-3.5 font-semibold text-gray-700 transition hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmAddToCart}
                  className="flex-1 rounded-lg bg-amber-700 px-4 py-3.5 font-semibold text-white transition hover:bg-amber-800"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
